<section class=" relative overflow-hidden" aria-labelledby="live-demo-heading">
  <!-- Section header -->
  <div class="container mx-auto px-4 md:px-6 relative z-10">
    <div class="text-center mb-4 lg:mb-16" data-aos="fade-up">
      <h2 id="live-demo-heading" class="section-title">Try our AI Now</h2>
      <p class="text-lg text-white/70 max-w-3xl mx-auto mt-4 hidden md:block">
        Experience the power of our AI-driven music matching with this interactive demo.
      </p>
    </div>

    <!-- Demo interface container -->
    <div class="max-w-4xl mx-auto bg-dark-gray rounded-2xl overflow-hidden shadow-glow border border-white/10"
         data-aos="fade-up">

      <!-- Demo form -->
      <div class="p-2 md:p-8">
        <h3 class="text-lg md:text-2xl font-bold text-white mb-2 md:mb-6">Upload an Image or Video</h3>

        <!-- File upload area with drag and drop support -->
        <input #fileInput (change)="onFilesSelected($event)"
               accept="image/*,video/*"
               multiple style="display: none"
               type="file">

        <div
            (click)="!isCompressingFiles && triggerFileInput()"
            (dragleave)="onDragLeave($event)"
            (dragover)="onDragOver($event)"
            (drop)="onDrop($event)"
            [class.border-electric-blue]="isDragging"
            [class.cursor-not-allowed]="isCompressingFiles"
            [class.opacity-50]="isCompressingFiles"
            [class.scale-[1.02]]="isDragging"
            [class.shadow-lg]="isDragging"
            class="mb-8 border-2 border-dashed border-white/20 rounded-xl p-2 text-center cursor-pointer
            hover:border-electric-blue/50 transition-all duration-200 bg-dark-blue/30 relative">

          <!-- Drag overlay indicator -->
          @if (isDragging) {
            <div class="absolute inset-0 bg-electric-blue/20 rounded-xl flex items-center justify-center z-10">
              <div class="flex flex-col items-center bg-dark-blue/80 p-4 rounded-lg shadow-lg">
                <svg class="h-12 w-12 text-electric-blue animate-bounce" fill="none" stroke="currentColor"
                     viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                </svg>
                <p class="text-white font-semibold text-lg mt-2">Release to Upload</p>
              </div>
            </div>
          }

          <!-- Display when no files are selected -->
          @if (selectedFiles.length === 0) {
            <div class="flex flex-col items-center justify-center">
              <svg class="h-12 w-12 text-white/50 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="1.5"/>
              </svg>
              <p class="text-white/70 mb-2">Drag and drop your file here, or</p>
              <button class="btn btn-secondary inline-flex items-center" type="button">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                     xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"/>
                </svg>
                Add Visuals
              </button>
              <p class="text-sm text-white/50 mt-4">Supports images (JPG, PNG, GIF) and videos (MP4, MOV) up
                to 100MB · Images are automatically compressed · Total upload limit: 100MB</p>
            </div>
          }

          <!-- Display file previews when files are selected -->
          @if (selectedFiles.length > 0) {
            <div class="file-preview-container relative max-h-80 overflow-y-auto pr-2 custom-scrollbar">
              <div
                  class="file-preview-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 sm:gap-3 md:gap-4 pb-4">
                @for (preview of previewItems; track preview.url; let i = $index) {
                  <div (click)="$event.stopPropagation()"
                       class="file-preview-item group relative rounded-lg sm:rounded-xl overflow-hidden 
                             bg-gradient-to-br from-dark-blue/80 to-dark-blue/40 
                             border border-white/20 backdrop-blur-sm
                             w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-28 lg:h-28
                             hover:scale-105 hover:shadow-2xl hover:shadow-electric-blue/25
                             hover:border-electric-blue/50 hover:from-electric-blue/20 hover:to-dark-blue/60
                             transition-all duration-300 ease-out
                             transform-gpu hover:z-10">

                    <!-- Shimmer effect overlay -->
                    <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
                                opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-300"></div>

                    <!-- Media content -->
                    <div class="relative w-full h-full flex items-center justify-center overflow-hidden">
                      <!-- Display image preview for image files -->
                      @if (preview.type === 'image') {
                        <img [src]="preview.url" alt="Image Preview"
                             class="w-full h-full object-cover transition-transform duration-300 
                                    group-hover:scale-110">
                      }

                      <!-- Display video preview for video files -->
                      @if (preview.type === 'video') {
                        <video [src]="preview.url" preload="metadata" muted
                               [attr.controls]="null"
                               class="w-full h-full object-cover transition-transform duration-300 
                                      group-hover:scale-110">
                        </video>
                      }
                    </div>

                    <!-- Remove button with enhanced styling - Hidden on mobile -->
                    @if (!isAnalyzing && !isCompressingFiles) {
                      <button (click)="removeFile(i); $event.stopPropagation();"
                              class="absolute top-1 right-1 bg-gradient-to-r from-red-500 to-red-600 
                                     text-white rounded-full shadow-lg border-2 border-white/20
                                     w-6 h-6 flex items-center justify-center 
                                     md:opacity-0 group-hover:opacity-100
                                     hover:scale-110 hover:from-red-600 hover:to-red-700 hover:shadow-xl
                                     transition-all duration-200 z-20 backdrop-blur-sm cursor-pointer"
                              title="Remove file">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    }

                    <!-- Loading overlay for processing states -->
                    <div class="absolute inset-0 bg-gradient-to-br from-electric-blue/20 to-transparent 
                                sm:opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                }

                <!-- Enhanced Add more button -->
                @if (!isAnalyzing && !isCompressingFiles) {
                  <div (click)="triggerFileInput()"
                       class="add-more-btn group relative rounded-lg sm:rounded-xl overflow-hidden 
                              bg-gradient-to-br from-white/10 to-white/5 
                              border-2 border-dashed border-white/30 backdrop-blur-sm
                              w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-28 lg:h-28
                              hover:scale-105 hover:shadow-xl hover:shadow-electric-blue/20
                              hover:border-electric-blue hover:from-electric-blue/20 hover:to-electric-blue/10
                              transition-all duration-300 ease-out cursor-pointer
                              flex flex-col items-center justify-center
                              transform-gpu hover:z-10">

                    <!-- Animated plus icon -->
                    <div class="relative flex items-center justify-center">
                      <svg class="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-8 lg:h-8 text-white/60 
                                 group-hover:text-electric-blue group-hover:scale-110
                                 transition-all duration-300"
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                      </svg>

                      <!-- Pulsing ring animation -->
                      <div class="absolute inset-0 rounded-full border-2 border-electric-blue/30 
                                  opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
                    </div>

                    <!-- Enhanced label -->
                    <span class="text-xs sm:text-sm text-white/50 group-hover:text-electric-blue/80 
                                 transition-colors duration-300 font-medium mt-1">
                      Add More
                    </span>

                    <!-- Shimmer effect -->
                    <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
                                opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-300"></div>
                  </div>
                }
              </div>

              <!-- Enhanced total size indicator with mobile delete -->
              <div class="size-indicator mt-4 flex items-center justify-between md:justify-center gap-3">
                <div class="bg-gradient-to-r from-dark-blue/80 to-dark-blue/60 backdrop-blur-sm 
                            rounded-full px-4 py-2 border border-white/20 shadow-lg">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-electric-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                    </svg>
                    <span class="text-sm text-white/80 font-medium">
                      {{ (totalUploadSize / (1024 * 1024)).toFixed(1) }}MB 
                      <span class="text-white/50">/ {{ TOTAL_SIZE_LIMIT / 1024 / 1024 }}MB</span>
                    </span>
                  </div>
                </div>

                @if (!isAnalyzing && !isCompressingFiles && selectedFiles.length > 0) {
                  <button (click)="removeAllFiles(); $event.stopPropagation()"
                          class="bg-gradient-to-r from-red-500 to-red-600
                               hover:from-red-600 hover:to-red-700 text-white rounded-full
                               w-8 h-8 md:hidden flex items-center justify-center
                               shadow-lg hover:shadow-xl border-2 border-white/20
                               hover:scale-110 transition-all duration-200 z-30 backdrop-blur-sm group"
                          title="Remove all files">
                    <svg class="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                }

              </div>

              <!-- Enhanced Remove All button -->
              @if (!isAnalyzing && !isCompressingFiles && selectedFiles.length > 0) {
                <button (click)="removeAllFiles(); $event.stopPropagation()"
                        class="absolute top-2 right-2 bg-gradient-to-r from-red-500 to-red-600
                               hover:from-red-600 hover:to-red-700 text-white rounded-full
                               w-8 h-8 hidden md:flex items-center justify-center
                               shadow-lg hover:shadow-xl border-2 border-white/20
                               hover:scale-110 transition-all duration-200 z-30 backdrop-blur-sm group"
                        title="Remove all files">
                  <svg class="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                       fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              }
            </div>
          }
        </div>

        <!-- Mobile Capture Options Modal -->
        @if (showMobileCaptureOptions) {
          <div class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
               (click)="cancelMobileCaptureOptions()">
            <div class="bg-gradient-to-br from-dark-blue via-dark-blue to-dark-gray rounded-2xl p-4 sm:p-6 
                        max-w-sm w-full max-h-[90vh] overflow-y-auto border border-white/20 shadow-2xl
                        min-w-[280px] mx-auto"
                 (click)="$event.stopPropagation()">

              <!-- Modal Header -->
              <div class="text-center mb-4 sm:mb-6">
                <h3 class="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Choose Option</h3>
                <p class="text-white/70 text-xs sm:text-sm">How would you like to add your content?</p>
              </div>

              <!-- Options -->
              <div class="space-y-2 sm:space-y-3">
                <!-- Take Picture - Only show if no videos selected or no files selected -->
                @if (currentMediaType === 'any' || currentMediaType === 'image') {
                  <button (click)="selectMobileCaptureOption('camera-photo')"
                          class="w-full bg-gradient-to-r from-electric-blue to-electric-blue/80 hover:from-electric-blue/80 hover:to-electric-blue
                                 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-200
                                 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl hover:scale-[1.02]
                                 text-sm sm:text-base">
                    <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    Take Picture
                  </button>
                }

                <!-- Take Video - Only show if no images selected or no files selected -->
                @if (currentMediaType === 'any' || currentMediaType === 'video') {
                  <button (click)="selectMobileCaptureOption('camera-video')"
                          class="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700
                                 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-200
                                 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl hover:scale-[1.02]
                                 text-sm sm:text-base">
                    <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                    Take Video
                  </button>
                }

                <!-- Open Gallery - Always show -->
                <button (click)="selectMobileCaptureOption('gallery')"
                        class="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700
                               text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-200
                               flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl hover:scale-[1.02]
                               text-sm sm:text-base">
                  <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  Open Gallery
                </button>
              </div>

              <!-- Cancel Button -->
              <button (click)="cancelMobileCaptureOptions()"
                      class="w-full mt-3 sm:mt-4 bg-white/10 hover:bg-white/20 text-white font-medium 
                             py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl
                             transition-all duration-200 border border-white/20 hover:border-white/30
                             text-sm sm:text-base">
                Cancel
              </button>
            </div>
          </div>
        }

        <!-- Image Compression Progress -->
        @if (isCompressingFiles) {
          <div class="mb-6 compression-progress-container">
            <div class="bg-gradient-to-r from-dark-blue/80 to-dark-blue/60 backdrop-blur-sm 
                        rounded-xl p-4 border border-white/20 shadow-lg">
              <div class="flex items-center justify-center gap-3">
                <div class="relative">
                  <svg class="animate-spin h-6 w-6 text-electric-blue" fill="none" stroke="currentColor"
                       viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                  </svg>
                  <div class="absolute inset-0 animate-ping">
                    <svg class="h-6 w-6 text-electric-blue/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                    </svg>
                  </div>
                </div>
                <div class="flex flex-col">
                  <span class="text-white font-medium">Compressing Images...</span>
                  <span class="text-white/60 text-sm">Optimizing file sizes for faster upload</span>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Gaming Mode Field - only visible when gaming mode is active -->
        @if (isGamingMode) {
          <div class="mb-6">
            <label class="block text-white/80 mb-2 text-sm font-medium">
              <span class="inline-flex items-center gap-2">
                <svg class="w-4 h-4 text-electric-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
                Game Name
              </span>
            </label>
            <input
                [(ngModel)]="gameContext"
                [disabled]="isAnalyzing"
                placeholder="Enter the name of the game you're playing (e.g., Minecraft, Cyberpunk 2077)"
                class="w-full bg-dark-blue border border-white/20 rounded-lg py-3 px-4 text-white/80
                focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue"
                type="text"
            />
            <p class="text-xs text-white/50 mt-1">This helps our AI find music that matches your gaming context</p>
          </div>
        }

        <!-- Text prompt - always visible -->
        <div class="mb-8">
          <label class="block text-white/80 mb-2 text-sm font-medium">Additional Context (Optional)</label>
          <textarea
              [(ngModel)]="textPrompt"
              [disabled]="isAnalyzing"
              [placeholder]="isMobile ? 'add context to refine your match (mood, audience, favorite artists etc)' : 'Add context or preferences to refine your match (mood, audience, reference artists, lyrical or musical traits, location, etc.)'"
              class="w-full bg-dark-blue border border-white/20 rounded-lg py-3 px-4 text-white/80
              focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue
              resize-none h-24 overflow-y-auto"
              rows="3"
              style="resize: none;"
          ></textarea>
        </div>

        <!-- Upload Progress Bar (only shows during active uploads) -->
        @if (showUploadProgress()) {
          <div class="mb-6 upload-progress-container">
            <div class="upload-progress-label flex justify-between text-sm text-white/70 mb-1">
              <span>Uploading files...</span>
              <span>{{ uploadProgress() | number:'1.0-0' }}%</span>
            </div>
            <div class="h-2 w-full bg-dark-blue/50 rounded-full overflow-hidden">
              <div
                  class="h-full bg-electric-blue rounded-full transition-all duration-300 ease-out flex items-center justify-end"
                  [style.width.%]="uploadProgress()">
                <div class="animate-pulse h-2 w-2 rounded-full bg-white mr-0.5"></div>
              </div>
            </div>
          </div>
        }

        <!-- Submit button with loading state -->
        <div class="flex lg:justify-center relative justify-around gap-2">
          <button
              (click)="analyze()"
              [class.opacity-50]="selectedFiles.length === 0"
              [disabled]="selectedFiles.length === 0 || isAnalyzing"
              class="px-2 lg:px-6 flex-grow lg:flex-grow-0 btn btn-primary inline-flex items-center gap-2 w-[180px] justify-center"
              type="button">
            @if (!isAnalyzing) {
              <span>{{ resultsAvailable() ? 'Rematch' : 'Match' }}</span>
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M14 5l7 7m0 0l-7 7m7-7H3" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
            } @else {
              <span class="analyzing-label flex items-center">
                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none"
                     viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Matching...</span>
              </span>
            }
          </button>
          @if (resultsAvailable() && !showSlideshowModal()) {
            <button
                (click)="reopenSlideshow()"
                class="px-2 lg:px-6 flex-grow lg:flex-grow-0 lg:absolute top-o right-0 btn btn-secondary btn-lg view-results-btn w-[180px]">
              <span>Open SlideShow</span>
            </button>
          }
        </div>

      </div>
    </div>

    <!-- Demo disclaimer -->
    <div class="text-center mt-8 text-sm text-white/50">
      This is a simplified demo. Enterprise solutions offer additional features and customization options.
      <a class="text-electric-blue hover:underline" href="#pricing">See pricing</a> for more information.
    </div>
  </div>
</section>

<!-- Enhanced Google Photos Memories-Style Slideshow -->
<app-slide-show
    (isVisibleChange)="onSlideshowVisibilityChange($event)"
    [isVisible]="showSlideshowModal()"
    [mediaItems]="enhancedSlideshowMediaItems()"
></app-slide-show>
